import { ServiceScraperResponse } from '../types/serviceScraperResponseTypes';
import {
    ServiceScrapingHistoryScope,
    StoredServiceScraping,
} from '../types/serviceScrapingHistoryTypes';

const databaseName = 'zitoneo-service-scraping';
const storeName = 'scrapingHistory';

const validateScope = ({ ownerId, hotelId }: ServiceScrapingHistoryScope) => {
    if (
        typeof ownerId !== 'string' ||
        !ownerId.trim() ||
        typeof hotelId !== 'string' ||
        !hotelId.trim()
    ) {
        throw new Error(
            'Owner and hotel IDs are required for scraping history.'
        );
    }
};

const openDatabase = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const request = indexedDB.open(databaseName, 1);
        let rejected = false;
        request.onupgradeneeded = () => {
            const store = request.result.createObjectStore(storeName, {
                keyPath: ['ownerId', 'hotelId', 'id'],
            });
            store.createIndex('ownerHotel', ['ownerId', 'hotelId']);
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => {
            rejected = true;
            reject(
                new Error(
                    'Scraping history database is blocked by another tab.'
                )
            );
        };
        request.onsuccess = () => {
            const database = request.result;
            if (rejected) {
                database.close();
            } else {
                database.onversionchange = () => database.close();
                resolve(database);
            }
        };
    });

const runTransaction = async <T>(
    mode: IDBTransactionMode,
    execute: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
    const database = await openDatabase();
    try {
        return await new Promise<T>((resolve, reject) => {
            const transaction = database.transaction(storeName, mode);
            const request = execute(transaction.objectStore(storeName));
            transaction.oncomplete = () => resolve(request.result);
            transaction.onabort = () =>
                reject(
                    transaction.error ||
                        new Error('Scraping history transaction aborted.')
                );
        });
    } finally {
        database.close();
    }
};

/**
 * Appends an original scraper response to the hotel scraping history.
 * @param scope Owner and hotel that requested the analysis.
 * @param response Original response returned by the scraper.
 * @returns The history entry after its transaction commits.
 */
export const saveServiceScraping = async (
    scope: ServiceScrapingHistoryScope,
    response: ServiceScraperResponse
): Promise<StoredServiceScraping> => {
    validateScope(scope);
    if (
        !response ||
        typeof response.Result !== 'boolean' ||
        !Array.isArray(response.Results)
    ) {
        throw new Error('Invalid service scraper response.');
    }
    const record: StoredServiceScraping = {
        ...scope,
        id: crypto.randomUUID(),
        analyzedAt: new Date().toISOString(),
        response,
    };
    await runTransaction('readwrite', store => store.add(record));
    return record;
};

/**
 * Loads original scraper responses for one owner and hotel, newest first.
 * @param scope Owner and hotel whose history should be loaded.
 * @returns History records that can be adapted against the current hotel data.
 */
export const getServiceScrapingHistory = async (
    scope: ServiceScrapingHistoryScope
): Promise<StoredServiceScraping[]> => {
    validateScope(scope);
    const records = await runTransaction<StoredServiceScraping[]>(
        'readonly',
        store =>
            store.index('ownerHotel').getAll([scope.ownerId, scope.hotelId])
    );
    return records.sort((first, second) =>
        second.analyzedAt.localeCompare(first.analyzedAt)
    );
};
